import type { ComboboxItem } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversationsEmployees } from '@/entities/conversations';
import { getShortName } from '@/shared/lib';
import { AllOrSpecificMultiSelect } from '@/shared/ui';

/**
 * Верхняя граница загружаемого списка операторов. Список берётся целиком, а поиск в выпадающем
 * списке — клиентский (как у выбора автора комментария). При большем числе операторов выдача
 * усекается лимитом — см. рекомендацию в контексте задачи.
 */
const OPERATORS_LIMIT = 100;

/** Пропсы выбора оператора в поиске по репликам (множественный выбор с пунктом «Все») */
export type OperatorSelectProps = {
  /** Выбранные операторы; пустой массив — «Все» (все консультанты) */
  value: number[];
  /** Смена набора операторов — управляющее действие уровня запроса (список перечитывается) */
  onChange: (employeeIds: number[]) => void;
  className?: string;
};

/**
 * Выпадающий список выбора оператора в поиске по репликам (множественный выбор с галочками).
 * Показывается только при говорящем «Консультант». В списке — сотрудники (`person.role=EMPLOYEE`)
 * из эндпоинта операторов; подпись собирается из заполненных частей имени.
 */
export const OperatorSelect: FC<OperatorSelectProps> = ({ value, onChange, className }) => {
  const { t } = useTranslation(['dialogs', 'common']);
  const { data, isLoading } = useConversationsEmployees({ limit: OPERATORS_LIMIT });

  const options = useMemo<ComboboxItem[]>(
    () =>
      (data?.employees ?? []).map((employee) => ({
        value: employee.id,
        label:
          getShortName(employee.firstName, employee.lastName, employee.middleName) ||
          t('dialogs:phraseSearch.operator.unnamed'),
      })),
    [data?.employees, t],
  );

  return (
    <AllOrSpecificMultiSelect
      options={options}
      value={value}
      onChange={onChange}
      allLabel={t('dialogs:phraseSearch.operator.all')}
      ariaLabel={t('dialogs:phraseSearch.operator.label')}
      searchPlaceholder={t('common:search')}
      emptyMessage={t('dialogs:phraseSearch.operator.empty')}
      isLoading={isLoading}
      className={className}
    />
  );
};
